import queryString from 'query-string'
import { toast } from '@/components/toasts/toast.helpers'
import { setOrganization } from '@/reducers/organizations/organization.redux'
import { setPlaybooks } from '@/reducers/playbooks/playbooks.redux'
import { fetchingAPI, apiService } from '@/api'
import { setOrganizationVoipCampaigns, setLoading } from './organizationVoipCampaigns.redux'

export const fetchOrganizationsAndConfigs =
  ({ organizationId }) =>
  async (dispatch) => {
    const configProperties = queryString.stringify({
      requested_properties: 'id,name,cid,organization_id,config_active',
    })

    const loadOrg = fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}`,
      'GET',
      dispatch
    )

    const loadAllConfigs = fetchingAPI(
      `${apiService.core}/api/${organizationId}/configs?${configProperties}`,
      'GET',
      dispatch
    )

    try {
      const [organization, configs] = await Promise.all([loadOrg, loadAllConfigs])

      dispatch(setOrganization(organization))
      dispatch(setPlaybooks(configs))
    } catch (err) {
      toast.error('Failed to fetch organization or configs', { cause: err })
    }
  }

export const fetchAllOrganizationVoipCampaigns =
  ({ organizationId }) =>
  async (dispatch) => {
    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/voip_campaigns?filter=all_campaigns`,
        'GET',
        dispatch
      )

      dispatch(
        setOrganizationVoipCampaigns({
          allCampaigns: response.all_campaigns || [],
          mappedCampaigns: response.mapped_campaigns || [],
          unmappedCampaigns: response.unmapped_campaigns || [],
        })
      )

      return response
    } catch (err) {
      toast.error('Failed to fetch VoIP campaigns', { cause: err })
      throw err
    }
  }

export const createNewVoipCampaign =
  ({ cid, clearForm, campaignName, organizationId, reloadOrganizationsVoipCampaigns }) =>
  async (dispatch) => {
    dispatch(setLoading({ modal: true }))
    try {
      const normalizedCampaignName = Array.isArray(campaignName)
        ? campaignName.map((item) => (typeof item === 'string' ? item : item.value))
        : campaignName

      await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/voip_campaigns`,
        'POST',
        dispatch,
        JSON.stringify({ cid, campaignName: normalizedCampaignName })
      )

      if (clearForm) clearForm()
      if (reloadOrganizationsVoipCampaigns) reloadOrganizationsVoipCampaigns()

      toast.success('VoIP campaign created successfully')
    } catch (err) {
      toast.error(`Failed to create VoIP campaign. ${err?.error_message}`, { cause: err })
    } finally {
      dispatch(setLoading({ modal: false }))
    }
  }

export const updateVoipCampaign =
  ({ organizationId, voipCampaignUuid, campaignName, cid, reloadOrganizationsVoipCampaigns }) =>
  async (dispatch) => {
    dispatch(setLoading({ modal: true }))
    try {
      await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/voip_campaigns/${voipCampaignUuid}`,
        'PATCH',
        dispatch,
        JSON.stringify({ campaignName, cid })
      )

      if (reloadOrganizationsVoipCampaigns) reloadOrganizationsVoipCampaigns()

      toast.success('VoIP campaign updated successfully')
    } catch (err) {
      toast.error(`Failed to update VoIP campaign. ${err?.error}`, { cause: err })
    } finally {
      dispatch(setLoading({ modal: false }))
    }
  }

export const deleteOrganizationsVoipCampaign =
  ({ organizationId, voipCampaignUuid, reloadOrganizationsVoipCampaigns }) =>
  async (dispatch) => {
    dispatch(setLoading({ modal: true }))
    try {
      await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/voip_campaigns/${voipCampaignUuid}`,
        'DELETE',
        dispatch
      )

      if (reloadOrganizationsVoipCampaigns) reloadOrganizationsVoipCampaigns()

      toast.success('VoIP campaign deleted successfully')
    } catch (err) {
      toast.error('Failed to delete VoIP campaign', { cause: err })
    } finally {
      dispatch(setLoading({ modal: false }))
    }
  }

export const massUpdateVoipCampaigns =
  ({
    organizationId,
    campaignUuids = [],
    campaignNames = [],
    cid,
    reloadOrganizationsVoipCampaigns,
  }) =>
  async (dispatch) => {
    dispatch(setLoading({ modal: true }))
    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/voip_campaigns/mass_edit_delete_voip_campaigns`,
        'PATCH',
        dispatch,
        JSON.stringify({
          campaign_uuids: campaignUuids,
          campaign_names: campaignNames,
          cid,
        })
      )

      if (reloadOrganizationsVoipCampaigns) reloadOrganizationsVoipCampaigns()

      const totalCount = response.total_processed || response.updated_campaigns?.length || 0
      toast.success(`${totalCount} VoIP campaign(s) updated successfully`)

      if (response.not_found_campaigns?.length > 0) {
        toast.warning(`${response.not_found_campaigns.length} campaign(s) not found`)
      }

      if (response.errors?.length > 0) {
        toast.warning(`${response.errors.length} campaign(s) had errors`)
      }

      return response
    } catch (err) {
      toast.error(`Failed to update VoIP campaigns. ${err?.error || err?.error_message}`, {
        cause: err,
      })
      throw err
    } finally {
      dispatch(setLoading({ modal: false }))
    }
  }

export const massDeleteVoipCampaigns =
  ({ organizationId, campaignUuids, reloadOrganizationsVoipCampaigns }) =>
  async (dispatch) => {
    dispatch(setLoading({ modal: true }))
    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/voip_campaigns/mass_edit_delete_voip_campaigns`,
        'DELETE',
        dispatch,
        JSON.stringify({ campaign_uuids: campaignUuids })
      )

      if (reloadOrganizationsVoipCampaigns) reloadOrganizationsVoipCampaigns()

      const deletedCount = response.deleted_campaigns?.length || 0
      toast.success(`${deletedCount} VoIP campaign(s) deleted successfully`)

      if (response.not_found_campaigns?.length > 0) {
        toast.warning(`${response.not_found_campaigns.length} campaign(s) not found`)
      }
    } catch (err) {
      toast.error(`Failed to delete VoIP campaigns. ${err?.error || err?.error_message}`, {
        cause: err,
      })
    } finally {
      dispatch(setLoading({ modal: false }))
    }
  }

export const uploadVoipCampaignCSV = (organizationId, parsedRows, dryRun) => async (dispatch) => {
  try {
    const csvData = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/voip_campaigns/upload?dry=${Boolean(dryRun)}`,
      'POST',
      dispatch,
      JSON.stringify(parsedRows)
    )
    return csvData
  } catch (err) {
    toast.error('Failed to upload CSV', { cause: err })
    throw err
  }
}
